/**
 * /feedback - tenant feedback intake (OCP-598).
 *
 * Every bug / feedback / feature report a tenant relayed to the control
 * plane: tenant, kind, status (pending_ticket / ticket_created /
 * ticket_failed), workspace ticket key, page, version. Filters by kind +
 * status; a row opens the detail with the verbatim body, the report facts,
 * the attachments (download) and the operator replies synced from the
 * intake ticket. "Retry ticket" re-enqueues the workspace job.
 *
 * The body is tenant-supplied data. It is rendered as plain text in a
 * <pre>, never as HTML or Markdown.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface ReportRow {
  id: string;
  tenant_id: string;
  tenant_subdomain: string;
  instance_id: string;
  kind: 'bug' | 'feedback' | 'feature';
  title: string;
  status: 'pending_ticket' | 'ticket_created' | 'ticket_failed';
  operator_ticket_key: string | null;
  ticket_attempts: number;
  last_ticket_error: string | null;
  /** OCP-748 - what the tenant instance was last told (received / in_work / fixed_in / answered) and why pushing stopped. */
  tenant_status: string | null;
  tenant_fixed_in_version: string | null;
  tenant_pushed_at: string | null;
  tenant_push_error: string | null;
  tenant_push_stopped: string | null;
  page: string | null;
  version: string | null;
  reply_count: number;
  attachment_count: number;
  created_at: string;
}

interface ReportDetail extends Omit<ReportRow, 'reply_count' | 'attachment_count'> {
  body: string;
  meta: Record<string, unknown>;
  attachments: Array<{
    id: string;
    position: number;
    filename: string;
    mimetype: string;
    size_bytes: number;
    stored_in: 'object_store' | 'inline';
    workspace_attachment_id: string | null;
  }>;
  replies: Array<{ id: string; body: string; authored_at: string; synced_at: string }>;
}

const KINDS = ['bug', 'feedback', 'feature'] as const;
const STATUSES = ['pending_ticket', 'ticket_created', 'ticket_failed'] as const;

const STATUS_LABEL: Record<ReportRow['status'], string> = {
  pending_ticket: 'pending ticket',
  ticket_created: 'ticket created',
  ticket_failed: 'ticket failed',
};
const STATUS_CLASS: Record<ReportRow['status'], string> = {
  pending_ticket: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  ticket_created: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  ticket_failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

function fmtUtc(iso: string): string {
  return fmtDateTime(iso);
}

export function AdminFeedbackPage() {
  const [kind, setKind] = useState<'' | (typeof KINDS)[number]>('');
  const [status, setStatus] = useState<'' | (typeof STATUSES)[number]>('');
  const [items, setItems] = useState<ReportRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (kind) p.set('kind', kind);
    if (status) p.set('status', status);
    p.set('limit', '200');
    return `?${p.toString()}`;
  }, [kind, status]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, c] = await Promise.all([
        api<{ items: ReportRow[]; total: number }>(`/v1/admin/feedback-reports${queryString}`),
        api<{ counts: Record<string, number> }>('/v1/admin/feedback-reports/counts'),
      ]);
      setItems(list.items);
      setTotal(list.total);
      setCounts(c.counts);
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load feedback reports (HTTP ${err.status}).`);
      else setError('Could not load feedback reports.');
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Feedback intake</h1>
          <p className="text-sm text-gray-500">
            Reports tenants relayed to the control plane. The workspace ticket is a projection; this list is the source of truth.
          </p>
        </div>
        {counts && (
          <div className="flex gap-2 text-xs" data-testid="feedback-counts">
            {STATUSES.map((s) => (
              <span key={s} className={`rounded px-2 py-1 ${STATUS_CLASS[s]}`}>
                {STATUS_LABEL[s]}: {counts[s] ?? 0}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Kind</span>
          <select
            aria-label="Kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm"
          >
            <option value="">all</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Status</span>
          <select
            aria-label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm"
          >
            <option value="">all</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </label>
        <span className="ml-auto self-center text-xs text-gray-500">{total} report{total === 1 ? '' : 's'}</span>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {items === null ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-6 text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-6 text-sm text-gray-500">
          No feedback reports match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Received</th>
                <th className="px-3 py-2">Tenant</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Ticket</th>
                <th className="px-3 py-2">Page / version</th>
                <th className="px-3 py-2">Replies</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  onClick={() => setOpenId(it.id)}
                >
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600 dark:text-gray-400">{fmtUtc(it.created_at)}</td>
                  <td className="px-3 py-2">
                    <Link to={`/tenants/${it.tenant_id}`} onClick={(e) => e.stopPropagation()} className="text-brand-600 hover:underline dark:text-brand-400">
                      {it.tenant_subdomain}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.kind}</td>
                  <td className="max-w-md truncate px-3 py-2 text-gray-900 dark:text-gray-100" title={it.title}>{it.title}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] ${STATUS_CLASS[it.status]}`}>{STATUS_LABEL[it.status]}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{it.operator_ticket_key ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">
                    {it.page ?? '—'} <span className="text-gray-400">/</span> {it.version ?? '—'}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-gray-400">{it.reply_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <ReportDetailModal id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function ReportDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => Promise<void> }) {
  const [report, setReport] = useState<ReportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ report: ReportDetail }>(`/v1/admin/feedback-reports/${id}`);
      setReport(r.report);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load the report (HTTP ${err.status}).` : 'Could not load the report.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function retry() {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await api(`/v1/admin/feedback-reports/${id}/retry-ticket`, { method: 'POST' });
      setNotice('Ticket job enqueued.');
      await load();
      await onChanged();
    } catch (err) {
      setNotice(err instanceof ApiError ? `Retry failed (HTTP ${err.status}).` : 'Retry failed.');
    } finally {
      setBusy(false);
    }
  }

  // OCP-751 - deliver status + every public reply to the instance again (after a tenant-side backfill or restore)
  const [resendReason, setResendReason] = useState('');
  async function resend() {
    if (busy || resendReason.trim().length < 3) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await api<{ push: { result: string; replies?: number } }>(`/v1/admin/feedback-reports/${id}/tenant-push`, {
        method: 'POST',
        body: { reason: resendReason.trim() },
      });
      setNotice(`Re-sent to the instance: ${res.push.result}${typeof res.push.replies === 'number' ? ` (${res.push.replies} replies)` : ''}.`);
      setResendReason('');
      await load();
      await onChanged();
    } catch (err) {
      setNotice(err instanceof ApiError ? `Re-send failed (HTTP ${err.status}).` : 'Re-send failed.');
    } finally {
      setBusy(false);
    }
  }

  const meta = report?.meta ?? {};
  const s = (v: unknown): string => (typeof v === 'string' && v ? v : '—');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Feedback report"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl text-sm"
      >
        {error && <p role="alert" className="text-red-600 dark:text-red-400">{error}</p>}
        {!report && !error && <p className="text-gray-500">Loading…</p>}
        {report && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{report.kind} · {report.tenant_subdomain}</p>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{report.title}</h2>
              </div>
              <span className={`rounded px-1.5 py-0.5 text-[11px] ${STATUS_CLASS[report.status]}`}>{STATUS_LABEL[report.status]}</span>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
              <Fact k="report id" v={report.id} mono />
              <Fact k="ticket" v={report.operator_ticket_key ?? '—'} mono />
              <Fact k="attempts" v={String(report.ticket_attempts)} />
              <Fact k="instance" v={report.instance_id} mono />
              <Fact k="version" v={s(meta.version)} mono />
              <Fact k="page" v={s(meta.page)} mono />
              <Fact k="reporter hash" v={s(meta.reporterHash)} mono />
              <Fact k="locale" v={s(meta.locale)} />
              <Fact k="submitted" v={typeof meta.submittedAt === 'string' ? fmtUtc(meta.submittedAt) : '—'} />
              <Fact
                k="tenant sees"
                v={`${report.tenant_status ?? 'received'}${report.tenant_fixed_in_version ? ` ${report.tenant_fixed_in_version}` : ''}`}
              />
              <Fact k="pushed" v={report.tenant_pushed_at ? fmtUtc(report.tenant_pushed_at) : '—'} />
            </dl>
            {(report.tenant_push_stopped || report.tenant_push_error) && (
              <p className="rounded bg-amber-50 dark:bg-amber-950/40 p-2 text-xs text-amber-800 dark:text-amber-300">
                {report.tenant_push_stopped === 'report_not_found'
                  ? 'The instance no longer holds this report - status and replies are not pushed any more.'
                  : report.tenant_push_stopped === 'replies_limit'
                    ? 'The instance holds 200 replies for this report - only status changes are pushed.'
                    : `Last push to the instance failed (retried on the next sync): ${report.tenant_push_error}`}
              </p>
            )}
            {report.last_ticket_error && (
              <p className="rounded bg-red-50 dark:bg-red-950/40 p-2 text-xs text-red-700 dark:text-red-300">
                Last ticket error: {report.last_ticket_error}
              </p>
            )}

            <section>
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Report (verbatim, tenant-supplied)</h3>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 dark:bg-gray-800 p-3 font-mono text-xs text-gray-800 dark:text-gray-200">{report.body}</pre>
            </section>
            {(['steps', 'expected', 'actual'] as const).map((k) =>
              typeof meta[k] === 'string' && (meta[k] as string).trim() ? (
                <section key={k}>
                  <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">{k}</h3>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 dark:bg-gray-800 p-3 font-mono text-xs text-gray-800 dark:text-gray-200">{meta[k] as string}</pre>
                </section>
              ) : null,
            )}

            <section>
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Attachments ({report.attachments.length})</h3>
              {report.attachments.length === 0 ? (
                <p className="text-xs text-gray-500">none</p>
              ) : (
                <ul className="space-y-1 text-xs">
                  {report.attachments.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-2">
                      <a
                        href={`/v1/admin/feedback-reports/${report.id}/attachments/${a.id}`}
                        className="font-mono text-brand-600 hover:underline dark:text-brand-400"
                      >
                        {a.filename}
                      </a>
                      <span className="text-gray-500">{a.mimetype} · {Math.ceil(a.size_bytes / 1024)} KB · {a.stored_in}</span>
                      <span className={a.workspace_attachment_id ? 'text-emerald-600' : 'text-amber-600'}>
                        {a.workspace_attachment_id ? 'on ticket' : 'not on ticket'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Operator replies synced to the tenant ({report.replies.length})</h3>
              {report.replies.length === 0 ? (
                <p className="text-xs text-gray-500">none yet - a PUBLIC comment on the intake ticket appears here within 5 minutes.</p>
              ) : (
                <ul className="space-y-2">
                  {report.replies.map((r) => (
                    <li key={r.id} className="rounded border border-gray-200 dark:border-gray-800 p-2">
                      <p className="mb-1 text-[11px] text-gray-500">{fmtUtc(r.authored_at)}</p>
                      <pre className="whitespace-pre-wrap break-words font-mono text-xs text-gray-800 dark:text-gray-200">{r.body}</pre>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {report.status === 'ticket_created' && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={resendReason}
                  onChange={(e) => setResendReason(e.target.value)}
                  placeholder="Reason for re-sending to the instance"
                  aria-label="Reason for re-sending to the instance"
                  className="min-w-0 flex-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  onClick={resend}
                  disabled={busy || resendReason.trim().length < 3}
                  className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  Re-send to instance
                </button>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              {notice && <span className="mr-auto text-xs text-gray-500">{notice}</span>}
              <button
                type="button"
                onClick={retry}
                disabled={busy}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {report.status === 'ticket_created' ? 'Re-upload attachments' : 'Retry ticket'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-gray-500">{k}</dt>
      <dd className={`truncate text-gray-800 dark:text-gray-200 ${mono ? 'font-mono' : ''}`} title={v}>{v}</dd>
    </div>
  );
}
